import { useQuery } from '@tanstack/react-query';
import { api, can } from '../lib/api';
import type { Site } from '../lib/types';
import { ApiError } from '../lib/api';
import { Badge, Banner, CopyButton, LoadError, Skeleton } from '../lib/ui';
import { Builder } from '../features/banner/Builder';
import { BlockingScreen } from '../features/blocking/BlockingScreen';
import { ScanScreen } from '../features/scan/ScanScreen';
import { StatsScreen } from '../features/stats/StatsScreen';
import { SetupChecklist } from '../features/setup/SetupChecklist';
import { ProofsScreen } from '../features/proofs/ProofsScreen';
import { SettingsScreen } from '../features/settings/SettingsScreen';
import { Hilfe } from '../features/help/Help';
import { t } from '../lib/i18n';

export type Tab = 'stats' | 'banner' | 'blocking' | 'scan' | 'proofs' | 'settings';
// Numbers first: the dashboard is what someone opens daily, the editor is what they open
// when something needs changing.
// The German label is the translation key, so the array stays a constant and `t()` runs
// per render — at module scope it would freeze whichever language was current at import.
const TABS: [Tab, string][] = [
  ['stats', 'Auswertung'],
  ['banner', 'Banner'],
  ['blocking', 'Blockieren & Anbieter'],
  // Next to the rules it feeds, but not inside them: a run takes minutes and is watched,
  // and it was previously a card at the bottom of the longest screen in the console.
  ['scan', 'Scan'],
  // Last because it is opened on demand — when a request arrives — not daily.
  ['proofs', 'Nachweise'],
  // Last of all, and hidden from a role that may change nothing on it: an empty tab full
  // of disabled fields is worse than no tab.
  ['settings', 'Einstellungen'],
];

export function SiteDetail({ siteId, tab, onTab }: {
  siteId: string;
  tab: Tab;
  onTab: (t: Tab) => void;
}) {
  const site = useQuery({ queryKey: ['site', siteId], queryFn: () => api<Site>(`/v1/sites/${siteId}`) });

  if (site.isPending) {
    return (
      <div className="space-y-4">
        <Skeleton rows={1} className="max-w-md" />
        <Skeleton rows={4} />
      </div>
    );
  }
  if (site.isError || !site.data) {
    // One message for four different situations used to tell someone whose network had
    // dropped that their website had been deleted.
    const status = site.error instanceof ApiError ? site.error.status : 0;
    return status === 404 ? (
      <Banner kind="error">
        {t('Diese Website gibt es nicht (mehr).')}{' '}
        <a className="underline" href="#/sites">{t('Zur Übersicht')}</a>
      </Banner>
    ) : status === 403 ? (
      <Banner kind="error">
        {t('Für diese Website haben Sie keine Berechtigung.')}{' '}
        <a className="underline" href="#/sites">{t('Zur Übersicht')}</a>
      </Banner>
    ) : (
      <LoadError what={t('Diese Website')} onRetry={() => void site.refetch()} />
    );
  }
  return (
    <div className="space-y-4">
      {/* Above the tabs, not inside one: a half-set-up site should say so wherever you
          land, and the card removes itself once every step is done. */}
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <div className="flex flex-wrap items-baseline gap-x-3">
          <a href="#/sites" className="text-xs text-[var(--color-muted)] hover:underline">{t('← Websites')}</a>
          <h1 className="flex items-center text-xl font-semibold">
            {site.data.name}
            <Hilfe to="einstieg" />
          </h1>
          <span className="text-[13px] text-[var(--color-muted)]">{site.data.primary_domain}</span>
          {/* Both identifiers, side by side and labelled, because they are not
              interchangeable and the page used to offer only one. The key goes in the
              snippet and the CDN path; the id addresses this site in the admin API. An
              integrator who found only the key here put it in /v1/sites/{id}/… and got a
              string of 500s for it. */}
          <CopyButton value={site.data.site_key} label={t('Site-Schlüssel')} />
          <CopyButton value={site.data.id} label={t('Site-ID (API)')} />
        </div>
        {site.data.config_version ? (
          <Badge tone="live">{t('live')}</Badge>
        ) : (
          <Badge tone="note">{t('nie veröffentlicht')}</Badge>
        )}
      </div>

      <SetupChecklist site={site.data} onGoTo={onTab} />

      <nav className="flex gap-1 border-b border-[var(--color-line)]" aria-label={t('Bereiche')}>
        {TABS
          .filter(([id]) => id !== 'settings' || can('site:update') || can('site:delete'))
          .map(([id, label]) => (
            <button
              key={id}
              onClick={() => onTab(id)}
              // The active tab sits on the rule rather than above it, so the section below
              // reads as the same surface rather than as a separate card.
              className={`-mb-px border-b-2 px-3 py-2 text-[13px] transition-colors ${
                tab === id
                  ? 'border-[var(--color-brand)] font-medium'
                  : 'border-transparent text-[var(--color-muted)] hover:text-[var(--color-ink)]'
              }`}
            >
              {t(label)}
            </button>
          ))}
      </nav>
      {tab === 'stats' && <StatsScreen site={site.data} />}
      {tab === 'banner' && <Builder site={site.data} />}
      {tab === 'blocking' && <BlockingScreen site={site.data} />}
      {tab === 'scan' && <ScanScreen site={site.data} />}
      {tab === 'proofs' && <ProofsScreen site={site.data} />}
      {tab === 'settings' && <SettingsScreen site={site.data} />}
    </div>
  );
}
